'use client';

import { useEffect, useMemo, type ReactNode } from 'react';
import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import type { IPromoBanner } from '@/sanity/lib/definitions';
import { resolveI18nRichtext } from '@/sanity/globals/I18n';
import { usePromoPopupOnce } from '@/hooks/usePromoPopupOnce';
import { useBannerCountdown } from '@/hooks/useBannerCountdown';
import { getCountdownLabels } from '@/lib/bannerCountdown';
import { sanitizeAffCode } from '@/lib/tradingCompetitionAffiliate';
import { Fog, RibbonLayer, type RibbonPlacement } from './SeasonalLayers';
import HalloweenPopupScene from './HalloweenPopupScene';
import BlackMonthPopupScene from './BlackMonthPopupScene';
import { SeasonalCodeBox, SeasonalCountdownBoxes, getSeasonalCopy } from './SeasonalCountdown';
import styles from './seasonal.module.css';

/**
 * Promo popup, seasonal skins of the "New UI 2026" design — Halloween 2026
 * and Black Month 2026 (components/global/PromoBannerDialogHalloween.tsx,
 * PromoBannerDialogBlackMonth.tsx pick the theme).
 *
 * Built from the Figma frames of "IQ Capital | New UI 2026":
 *   Halloween   desktop 12770:42307 (971x552), mobile 12770:42359 (400x554)
 *   Black Month desktop 12770:42014 (971x552), mobile 12770:42070 (400x586)
 * and from the designer's handoff (public/previews/popups-handoff). Both
 * backgrounds are rebuilt from the Figma layers (HalloweenPopupScene.tsx,
 * BlackMonthPopupScene.tsx): the designer reworked the frames after the
 * handoff, so its flattened `arte@2x.webp` plates no longer match.
 * Everything else is real HTML laid out as in Figma, with the animated
 * layers of SeasonalLayers.tsx on top.
 *
 * Sizes are the Figma px divided by 8 (1rem = 8px at the design width, see
 * app/globals.scss). `lg:` is 993px here, so the 400-wide mobile frame is the
 * base and the desktop frame is the `lg:` override — same as the V1 popup.
 *
 * Behaviour is identical to PromoBannerDialogV1: the scroll-triggered,
 * once-a-day open state comes from usePromoPopupOnce, the copy from the
 * document's i18n fallback chain, the countdown window from the document's
 * countdown fields, and the CTA stays a CTAButton so its CTAWrapper keeps
 * resolving link/popup/anchor actions and the affiliate pages can keep
 * rewriting `data.button.cta.link`.
 *
 * Black Month has two modes. On an affiliate page (`affiliateCode` given,
 * from the page's `?aff=` hero CTA) the countdown gives way to the code box
 * of the "popup afiliado" frames; everywhere else it counts down like
 * Halloween. The Figma frames of the countdown variant were removed from the
 * file after the handoff, so that mode takes the affiliate frames' scene
 * with the countdown of the handoff's `bm-popup-*` references.
 */

export type SeasonalTheme = 'halloween' | 'blackMonth';

interface ThemeConfig {
  title: string;
  /** The theme's layered Figma background (HalloweenPopupScene / BlackMonthPopupScene). */
  scene: 'halloween' | 'blackMonth';
  /** Black Month only: an affiliate code replaces the countdown with the code box. */
  affiliateCode?: boolean;
  fog?: boolean;
  /** Desktop only: the satin ribbon, clipped to the left 649px of the card. */
  ribbon?: RibbonPlacement;
  /** Headline size: Halloween 40/32, Black Month 48/32 (Figma 12770:42334 / 12770:42040). */
  headlineClass: string;
  /** Mobile card height: Halloween 538 (12770:42360), Black Month 570 (12770:42071). Desktop is 538 for both. */
  mobileCardClass: string;
  /** Position and width of the text column, mobile then `lg:` desktop. */
  columnClass: string;
  /** Mobile countdown boxes relative to the desktop ones. */
  mobileCountdownScale: number;
  /** Position and width of the button, mobile then `lg:` desktop. */
  buttonClass: string;
}

const THEMES: Record<SeasonalTheme, ThemeConfig> = {
  halloween: {
    title: 'Halloween Special',
    scene: 'halloween',
    fog: true,
    headlineClass: 'text-[4rem] tracking-[-0.12rem] lg:text-[5rem] lg:tracking-[-0.15rem]',
    mobileCardClass: 'h-[67.25rem]',
    // 12770:42388: (31, 260) 330 wide — not centred in the 384 card (31 left,
    // 23 right), kept as drawn. 12770:42331: x 452, 446 wide, centred 48.26px
    // above the middle.
    columnClass:
      'left-[3.875rem] top-[32.5rem] w-[41.25rem] lg:left-[56.5rem] lg:top-[calc(50%-6.0325rem)] lg:w-[55.75rem]',
    // 12840:42818 boxes are 68.53 wide vs 83.477 on desktop.
    mobileCountdownScale: 0.8209,
    // 12770:42391: (16, 475.29) 354x46, 16.71 above the bottom; 12770:42352:
    // (452, 446) 450x46, 46 above it.
    buttonClass: 'bottom-[2.08875rem] left-[2rem] w-[44.25rem] lg:bottom-[5.75rem] lg:left-[56.5rem] lg:w-[56.25rem]',
  },
  blackMonth: {
    title: 'Black Month Special',
    scene: 'blackMonth',
    affiliateCode: true,
    // Figma 12940:8064: a 1953.24x115.02 ribbon rotated 34.92deg whose
    // bounding box sits at (-1018, -393) => centre (-184.3, 213.3) of the card.
    ribbon: { cx: -184.3, cy: 213.3, width: 1953.24, height: 115.02, angle: 34.92, opacity: 0.9 },
    headlineClass: 'text-[4rem] tracking-[-0.12rem] lg:text-[6rem] lg:tracking-[-0.18rem]',
    mobileCardClass: 'h-[71.25rem]',
    // 12770:42099: 354 wide with 12px side padding (330 inner) at y 295.52,
    // centred; 12770:42037: x 482, 446 wide, centred 24.26px above the middle.
    columnClass:
      'left-1/2 top-[36.94rem] w-[44.25rem] -translate-x-1/2 px-[1.5rem] lg:left-[60.25rem] lg:top-[calc(50%-3.0325rem)] lg:w-[55.75rem] lg:translate-x-0 lg:px-0',
    mobileCountdownScale: 0.7667,
    // 12770:42102: full column width 16 above the bottom; 12770:42058: (490, 470) 438x46, 22 above it.
    buttonClass:
      'bottom-[2rem] left-1/2 w-[44.25rem] -translate-x-1/2 lg:bottom-[2.75rem] lg:left-[61.25rem] lg:w-[54.75rem] lg:translate-x-0',
  },
};

/* -------------------------------------------------------------------------- */
/*  Rich text                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Same mapping as the V1 popup: the headline is Geist Medium white and every
 * serif/highlight mark the editors can reach becomes the Blacker Display
 * Italic #dcf8f0 accent ("3+1 &", "85% off"); the Normal paragraph is the
 * small line under it (Geist 18, 70% on desktop, 80% white on mobile).
 */
// `leading-[0]`: the serif run has other ascent/descent metrics than Geist,
// and an inline box of its own height would stretch the first line box by
// ~3px; collapsed, the paragraph's strut alone sets the 0.98 line, as in Figma.
const serifMark = ({ children }: { children?: ReactNode }) => (
  <span className="font-blacker font-normal italic leading-[0] text-[#dcf8f0]">{children}</span>
);

function buildTextComponents(headlineClass: string): PortableTextComponents {
  // The leading comes after the size classes: tailwind-merge treats a font
  // size as overriding an earlier `leading-*` (text-xl sets both).
  const Headline = ({ children }: { children?: ReactNode }) => (
    <p
      className={cn(
        'mb-0 w-full text-center font-geist font-medium text-white [word-break:break-word]',
        headlineClass,
        'leading-[0.98]',
      )}
    >
      {children}
    </p>
  );
  return {
    block: {
      h1: Headline,
      h2: Headline,
      h3: Headline,
      h4: Headline,
      h5: Headline,
      h6: Headline,
      // Mobile: Figma's auto line height for Geist 18 is 23px (Chrome's
      // `normal` is 27). Desktop: the frames trim the line box to the cap
      // height (Figma text-box-trim, 13px); without it the column grows 9px
      // and, centred, drifts the headline and the countdown 4.5px each.
      normal: ({ children }) => (
        <p className="mb-0 w-full text-center font-geist text-[2.25rem] font-normal leading-[2.875rem] tracking-[-0.0675rem] text-white/80 lg:leading-[1.625rem] lg:text-white lg:opacity-70 lg:[text-box-edge:cap_alphabetic] lg:[text-box-trim:trim-both]">
          {children}
        </p>
      ),
    },
    marks: {
      serif: serifMark,
      em: serifMark,
      lightGreenHighlight: serifMark,
      lightGreenSerif: serifMark,
      darkGreenSerif: serifMark,
      greenSerifHighlight: serifMark,
      strong: ({ children }) => <span className={cn('font-semibold', styles.inheritFont)}>{children}</span>,
      underline: ({ children }) => <u className="underline">{children}</u>,
    },
    unknownMark: ({ children }) => <>{children}</>,
  };
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export interface SeasonalPromoDialogProps {
  data: IPromoBanner;
  language?: string;
  /** Preview harnesses: open immediately, ignore the once-a-day stamp. */
  forceOpen?: boolean;
  /** Partner code of an affiliate page; Black Month shows it instead of the countdown. */
  affiliateCode?: string;
  theme: SeasonalTheme;
}

export default function SeasonalPromoDialog({
  data,
  language,
  forceOpen,
  affiliateCode,
  theme,
}: SeasonalPromoDialogProps) {
  const config = THEMES[theme];
  const [open, setOpen] = usePromoPopupOnce(forceOpen);
  const timeLeft = useBannerCountdown(data);

  // Same rule as the other popups: the translation wins on non-default
  // languages and falls back to the stored copy when that language is empty.
  const text = resolveI18nRichtext(data.text, data.textI18n, language);
  // Used as object keys, so they have to survive draft mode's stega characters.
  const cleanLanguage = stegaClean(language) || undefined;
  const labels = getCountdownLabels(cleanLanguage);
  const copy = getSeasonalCopy(cleanLanguage);

  // The code box exists in the Black Month design only.
  const code = config.affiliateCode ? sanitizeAffCode(stegaClean(affiliateCode)) : null;

  const textComponents = useMemo(() => buildTextComponents(config.headlineClass), [config.headlineClass]);

  // The live documents end on an empty "normal" block, which would otherwise
  // add a phantom row to the gapped content column.
  const blocks = useMemo(() => {
    if (!text) return undefined;
    const filtered = text.filter((block) => {
      const candidate = block as { _type?: string; children?: { text?: string }[] };
      if (candidate._type !== 'block') return true;
      return (candidate.children ?? []).some((child) => (child?.text ?? '').trim().length > 0);
    });
    return filtered.length ? filtered : undefined;
  }, [text]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'unset';
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* 400px/8 = 50rem mobile frame, 971px/8 = 121.375rem desktop frame */}
      {/* `!outline-none`: Radix focuses this container on open (auto-focus is
          prevented in the base) and Chrome draws the `outline-style: auto`
          focus ring even at the base's zero width — a stroke around the
          frame that is not in the design. */}
      <DialogContent
        hideCloseButton
        className="!w-[min(50rem,calc(100%-2rem))] !border-0 !bg-transparent !p-0 !outline-none !duration-700 lg:!w-[121.375rem]"
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{config.title}</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>

        {/* Glass frame — radius 11, 1px hairline at white 10%, grey 10% fill;
            7px padding on the mobile frame, 6px on the desktop one. */}
        <div className="flex flex-col items-start rounded-[1.375rem] border border-white/10 bg-[rgba(149,149,149,0.1)] p-[0.875rem] lg:p-[0.75rem]">
          {/* Inner card — 384 wide on mobile (radius 6; 538 tall for Halloween,
              570 for Black Month), 957x538 on desktop (radius 8). `isolate`
              keeps the blend modes compositing against this card only. */}
          <div
            className={cn(
              'relative isolate w-full overflow-hidden rounded-[0.75rem] bg-[#001b14] lg:h-[67.25rem] lg:rounded-[1rem]',
              config.mobileCardClass,
            )}
            data-seasonal-popup={theme}
            data-seasonal-mode={code ? 'code' : 'countdown'}
          >
            {/* ---------------- Artwork + animated layers ---------------- */}
            <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
              {config.scene === 'halloween' && <HalloweenPopupScene />}
              {config.scene === 'blackMonth' && <BlackMonthPopupScene />}

              {config.ribbon && (
                // Figma clips the ribbon on the left 649px of the card (handoff
                // "recortada na área (7, 7, 649 × 538)") so it never crosses the text.
                <div className="absolute left-0 top-0 hidden h-full w-[81.125rem] overflow-hidden lg:block">
                  <RibbonLayer
                    placement={config.ribbon}
                    frame={{ width: 957, height: 538 }}
                    entrance
                  />
                </div>
              )}

              {config.fog && <Fog />}
            </div>

            {/* ---------------- Text column ---------------- */}
            {/* Top-anchored on mobile, centred on its Figma midpoint on desktop;
                position and width per theme (columnClass). 12px between the
                headline and the subline, 32px more before the countdown on
                desktop (12770:42332 / 12770:42388). */}
            <div
              className={cn(
                'absolute flex flex-col items-center gap-[1.5rem] lg:-translate-y-1/2 lg:gap-[4rem]',
                config.columnClass,
              )}
            >
              {blocks && (
                // 12px/8 = 1.5rem between the headline and the subline.
                <div className="flex w-full flex-col items-center gap-[1.5rem]">
                  <PortableText value={blocks} components={textComponents} />
                </div>
              )}

              {code ? (
                <SeasonalCodeBox code={code.toUpperCase()} caption={copy.useCode} />
              ) : (
                timeLeft && (
                  <>
                    <SeasonalCountdownBoxes
                      timeLeft={timeLeft}
                      labels={labels}
                      scale={config.mobileCountdownScale}
                      className="lg:hidden"
                    />
                    <SeasonalCountdownBoxes
                      timeLeft={timeLeft}
                      labels={labels}
                      className="hidden lg:flex"
                    />
                  </>
                )
              )}
            </div>

            {/* ---------------- Button ---------------- */}
            {/* Figma 12770:42352 / 12770:42391: white, 46px tall, 2px radius,
                Geist SemiBold 14, capitalised; position and width per theme
                (buttonClass). */}
            {data.button && (
              <div className={cn('absolute', config.buttonClass)}>
                <CTAButton
                  data={data.button}
                  language={language}
                  overrideVariant="white"
                  className={cn(
                    '!w-full sm:!w-full',
                    '[&_.button]:!min-h-[5.75rem] [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem]',
                    '[&_.button]:!text-[1.75rem] [&_.button]:!font-semibold [&_.button]:!tracking-[-0.035rem]',
                    '[&_.button-text]:capitalize [&_.button-text-hover]:capitalize',
                  )}
                />
              </div>
            )}

            {/* ---------------- Close (12770:42355) ---------------- */}
            {/* 44x44 at (898, 16) on desktop; the mobile frames have no close
                in Figma, so the same box sits 16px from the corner. */}
            <DialogClose
              aria-label="Close"
              className="absolute right-[2rem] top-[2rem] flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-[0.5rem] bg-white/10 transition-colors hover:bg-white/20 lg:right-[1.875rem]"
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- exported glyph */}
              <img src="/promo-v1/close.svg" alt="" className="h-[3rem] w-[3rem]" />
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
